
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ShieldCheck, ShieldAlert, Car, QrCode, Smartphone, Hand, Camera, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useDateFormatter } from '@/shared/hooks';
import { useI18n } from '@/shared/i18n';
import { AccessLog, AccessStatus } from '@/shared/types';

interface AccessLogListProps {
  logs: AccessLog[];
  onRequestRefresh?: () => void;
}

interface PhotoGalleryProps {
  photos: string[];
  initialIndex: number;
  onClose: () => void;
}

const PhotoGallery: React.FC<PhotoGalleryProps> = ({ photos, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
  }, []);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < photos.length - 1 ? prev + 1 : prev));
  }, [photos.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, goToPrevious, goToNext]);

  if (photos.length === 0) return null;

  return (
    <div
      className="fixed inset-0 bg-black/95 flex flex-col z-50 animate-in fade-in duration-200"
      onClick={onClose}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3">
        <button
          onClick={onClose}
          className="p-2 hover:bg-white/10 rounded-full transition-colors"
        >
          <X className="text-white" size={24} />
        </button>
        <span className="text-white font-medium">
          {currentIndex + 1} / {photos.length}
        </span>
        <div className="w-10" />
      </div>

      {/* Gallery content */}
      <div
        className="flex-1 flex items-center justify-center relative px-16"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Previous button */}
        {currentIndex > 0 && (
          <button
            onClick={goToPrevious}
            className="absolute left-4 p-3 bg-black/30 hover:bg-black/50 rounded-full transition-colors"
          >
            <ChevronLeft className="text-white" size={32} />
          </button>
        )}

        {/* Image */}
        <img
          src={photos[currentIndex]}
          alt={`Foto ${currentIndex + 1}`}
          className="max-w-full max-h-[75vh] rounded-lg object-contain"
          onError={onClose}
        />

        {/* Next button */}
        {currentIndex < photos.length - 1 && (
          <button
            onClick={goToNext}
            className="absolute right-4 p-3 bg-black/30 hover:bg-black/50 rounded-full transition-colors"
          >
            <ChevronRight className="text-white" size={32} />
          </button>
        )}
      </div>

      {/* Dots indicator */}
      {photos.length > 1 && (
        <div className="flex justify-center gap-2 py-4">
          {photos.map((_, index) => (
            <button
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                setCurrentIndex(index);
              }}
              className={`w-2.5 h-2.5 rounded-full transition-all ${
                index === currentIndex
                  ? 'bg-white w-3 h-3'
                  : 'bg-white/40 hover:bg-white/60'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const AccessLogList: React.FC<AccessLogListProps> = ({ logs, onRequestRefresh }) => {
  const { t } = useI18n();
  const { formatDateTime } = useDateFormatter();
  const [galleryPhotos, setGalleryPhotos] = useState<string[]>([]);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const lastRefreshRef = useRef<number>(0);

  const openGallery = (photos: string[], index: number) => {
    setGalleryPhotos(photos);
    setGalleryIndex(index);
  };

  const closeGallery = () => {
    setGalleryPhotos([]);
    setGalleryIndex(0);
  };

  const getMethodIcon = (method: string) => {
    switch (method) {
      case 'LPR': return <Car size={16} />;
      case 'QR_RESIDENT':
      case 'QR_GUEST':
      case 'QR_HIK': return <QrCode size={16} />;
      case 'REMOTE_OPEN':
      case 'APP_REMOTE': return <Smartphone size={16} />;
      case 'FACEID':
      default: return <Hand size={16} />;
    }
  };

  if (logs.length === 0) {
    return <div className="text-center p-8 text-slate-500">No hay registros de acceso recientes.</div>;
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden animate-in fade-in duration-300">
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="bg-slate-50 text-slate-500 font-medium">
            <tr>
              <th className="p-4">{t('log.header.date')}</th>
              <th className="p-4">{t('log.header.user')}</th>
              <th className="p-4">{t('log.header.method')}</th>
              <th className="p-4">{t('log.header.entry')}</th>
              <th className="p-4">{t('log.header.status')}</th>
              <th className="p-4">{t('log.header.photos') || 'Fotos'}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {logs.map((log) => (
              <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                <td className="p-4 text-slate-600 font-mono text-xs">{formatDateTime(log.timestamp)}</td>
                <td className="p-4 font-medium text-slate-800">
                  {log.userName}
                  {log.details && <div className="text-xs text-slate-400 font-normal">{log.details}</div>}
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-2 text-slate-600">
                    {getMethodIcon(log.method)}
                    <span className="text-xs">{t(`log.method.${log.method.toLowerCase()}`)}</span>
                  </div>
                </td>
                <td className="p-4 text-slate-600">{log.entryPoint}</td>
                <td className="p-4">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${
                    log.status === AccessStatus.GRANTED
                      ? 'bg-green-50 text-green-700 border-green-200'
                      : 'bg-red-50 text-red-700 border-red-200'
                  }`}>
                    {log.status === AccessStatus.GRANTED ? <ShieldCheck size={12}/> : <ShieldAlert size={12}/>}
                    {t(`log.status.${log.status.toLowerCase()}`)}
                  </span>
                </td>
                <td className="p-4">
                  {(() => {
                    const allPhotos = [
                      ...(log.photos ?? []),
                      ...(log.photoUrl ? [log.photoUrl] : []),
                    ];
                    const handleImgError = () => {
                      const now = Date.now();
                      if (now - lastRefreshRef.current > 3000) {
                        lastRefreshRef.current = now;
                        onRequestRefresh?.();
                      }
                    };
                    return allPhotos.length > 0 ? (
                      <div className="flex items-center gap-2">
                        {allPhotos.slice(0, 3).map((photo, index) => (
                          <button
                            key={index}
                            onClick={() => openGallery(allPhotos, index)}
                            className="relative group"
                          >
                            <img
                              src={photo}
                              alt={`Foto ${index + 1}`}
                              className="w-10 h-10 rounded-lg object-cover ring-2 ring-transparent hover:ring-blue-400 transition-all"
                              onError={handleImgError}
                              loading="lazy"
                            />
                            {index === 2 && allPhotos.length > 3 && (
                              <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
                                <span className="text-white text-xs font-bold">+{allPhotos.length - 3}</span>
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <span className="text-slate-300">
                        <Camera size={16} />
                      </span>
                    );
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {galleryPhotos.length > 0 && (
        <PhotoGallery
          photos={galleryPhotos}
          initialIndex={galleryIndex}
          onClose={closeGallery}
        />
      )}
    </div>
  );
};
